/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import "./services/i18n";
// icons
import StudyFolderIcon from "./assets/icons/folder-tree-solid.svg";
import MinisIcon from "./assets/icons/square-minus-solid.svg";
import PlusIcon from "./assets/icons/square-plus-solid.svg";
import ViewerLightIcon from "./assets/icons/viewer_icon_light_mode.svg";
import ViewerDarkIcon from "./assets/icons/viewer_icon_dark_mode.svg";
import GoogleIcon from "./assets/icons/google-ic.svg";
import AppleIcon from "./assets/icons/apple-ic.svg";
import UserLargeIcon from "./assets/icons/user-large.svg";
import RushIcon from "./assets/icons/rush.svg";
import UnRushIcon from "./assets/icons/unrush.svg";
import RushLightIcon from "./assets/icons/rush-light.svg";
import UnRushLightIcon from "./assets/icons/unrush-light.svg";
import StatIcon from "./assets/icons/stat-icon.svg";
import HighIconSVG from "./assets/icons/high-icon.svg";
import RoutineIconSVG from "./assets/icons/routine-icon.svg";
import BrandCall from "./assets/icons/brands/call.svg";
import BrandLanguage from "./assets/icons/brands/language.svg";
import BrandLocation from "./assets/icons/brands/location_on.svg";
import BrandMail from "./assets/icons/brands/mail.svg";

import BrandCallDark from "./assets/icons/brands/call-dark.svg";
import BrandLanguageDark from "./assets/icons/brands/language-dark.svg";
import BrandLocationDark from "./assets/icons/brands/location_on-dark.svg";
import BrandMailDark from "./assets/icons/brands/mail-dark.svg";
/** Context */
import MeddreamWindowContext from "./context/MeddreamWindowContext";
import MeddreamWindow from "./context/MeddreamWindow";
import {
  useWSS,
  WebSocketProvider,
  WebSocketContextType,
  FHIRCastEvent,
  WssFHIRCastEventPayload,
} from "./context/Websocket";
import { CaptchaProviderContext, useCaptcha } from "./context/CaptchaContext";
/** Tooltips */
import { InformationTooltip } from "./components/Tooltips/InformationTooltip";
import { AHSTooltip } from "./components/Tooltips/AHSTooltip";
/** Components **/
import { LogoLong, LogoMain, LogoShort, CPLogoMain } from "./components/Logo";
import TypingEditor, {
  ITypingEditorRef,
} from "./components/Editor/TypingEditor";
import { AdvaPACSPrivacyAndPolicy } from "./components/AdvaPACSPrivacyAndPolicy";
import { SupportLink } from "./components/SupportLink";
import { TermsOfServiceLink } from "./components/TermsOfServiceLink";
import { RadPairEditor } from "./components/Editor/RadpairEditor";
import { IconPicker, IconDetail } from "./components/Icons/IconPicker";
import { IconDisplay } from "./components/IconDisplay";
import { UploadFileDropArea } from "./components/UploadFileDropArea";
import { PortalCard } from "./components/PatientPortal/PortalCard";
import { ReportCard } from "./components/PatientPortal/ReportCard";
import { ExamCard } from "./components/PatientPortal/ExamCard";
import { OptionLabel } from "./components/OptionLabel";
import {
  Loader,
  RestrictedIp,
  DomainError,
  AppError,
  ImageLoader,
  ViewerLoader,
} from "./components/Loader";
import { GenerateAccessionNumber } from "./components/GenerateAccessionNumber";
import { HighestIcon } from "./components/ReportPriorityIcons/HighestIcon";
import { HighIcon } from "./components/ReportPriorityIcons/HighIcon";
import { RoutineIcon } from "./components/ReportPriorityIcons/RoutineIcon";
import { MediumIcon } from "./components/ReportPriorityIcons/MediumIcon";
import { LowIcon } from "./components/ReportPriorityIcons/LowIcon";
import { LowestIcon } from "./components/ReportPriorityIcons/LowestIcon";
import { TextTruncated } from "./components/Displays/TextTruncated";
import { IconDisplay as ReportPriorityIconDisplay } from "./components/Displays/IconDisplay";
import { IconPicker as ReportPriorityIconPicker } from "./components/Displays/IconPicker";
import { ReportPriorityDisplay } from "./components/Displays/ReportPriorityDisplay";
import { ShowSearchRestrictionMessage } from "./components/Displays/ShowSearchRestrictionMessage";
import { MultiLevelDropdown } from "./components/Dropdown/MultiLevelDropdown";
import { StudyMultiLevelDropdown } from "./components/Dropdown/StudyMultiLevelDropdown";
import { AuthenticatorAppModal } from "./components/MFA/AuthenticatorAppModal";
import { PasskeyAuthenticateModal } from "./components/MFA/PasskeyAuthenticateModal";
import { MultiLevelDropdownButtons } from "./components/Dropdown/MultiLevelDropdownButtons";
import { BackButton } from "./components/Button/BackButton";
import { ConnectButton } from "./components/Button/ConnectButton";
import { BlockUI } from "./components/BlockUI";
import {
  CommonDatePicker,
  RenderCustomHeader,
} from "./components/CommonDatePicker";
import { ZedIcon } from "./components/Icons/ZedIcon";
import { LockSolidIcon } from "./components/Icons/LockSolidIcon";
import { Mobile3Icon } from "./components/Icons/Mobile3Icon";
import { PassKeyIcon } from "./components/Icons/PassKeyIcon";
import { DiagnosticReportFooter } from "./components/Report/DiagnosticReportFooter";
import { DiagnosticReportHeader } from "./components/Report/DiagnosticReportHeader";
import { DiagnosticReportButtons } from "./components/Report/DiagnosticReportButtons";
import {
  PriorReportSummaryList,
  PriorReportSummaryStatus,
} from "./components/Report/PriorReportSummaryList";
import type { PriorReportSummaryListProps } from "./components/Report/PriorReportSummaryList";
export { PriorReportSummaryList, PriorReportSummaryStatus };
export type { PriorReportSummaryListProps };
import { DocumentViewer } from "./components/Viewers/DocumentViewer";
import { HTMLViewer } from "./components/Viewers/HTMLViewer";
import { RadioButtonGroup } from "./components/RadioButtonGroup";
import { ReportViewer } from "./components/Viewers/ReportViewer";
import { BigText } from "./components/Displays/BigText";
import { Card } from "./components/Displays/Card";
import { IdleWrapper } from "./components/IdleWrapper";
import { CommitedStatus } from "./components/Displays/CommitedStatus";
import { HighlightedTextNotMatch } from "./components/Displays/HighlightedTextNotMatch";
import { PersonCircleXMarkIcon } from "./components/Displays/PersonCircleXMarkIcon";
import { ErrorAlert, SimpleAlert } from "./components/Alert";
import { ContentWrapperComponent } from "./components/ContentWrapperComponent";
import { ShowErrorMessage } from "./components/Displays/ShowErrorMessage";
import {
  TableFooter,
  PAGE_SIZE_OPTION,
} from "./components/DataTable/TableFooter";
import { TableView } from "./components/DataTable/TableView";
import { Table } from "./components/DataTable/Table";
import { CustomizeSettingTable } from "./components/DataTable/CustomizeSettingTable";
import TableLazy from "./components/DataTable/TableLazy";
import { PermissionTreeSelect } from "./components/PermissionSelect";
import { TableSelection } from "./components/DataTable/TableSelection";
import { Draggable, DropTarget } from "./components/DragAndDrop";
import { ProgressBar } from "./components/Displays/ProgressBar";
import { ProgressBarBadgeTooltip } from "./components/Displays/ProgressBarBadgeTooltip";
import { PasswordRequirements } from "./components/Displays/PasswordRequirements";
import { PasswordRequirementTooltip } from "./components/Displays/PasswordRequirementTooltip";
import { MoveDetailModal } from "./components/Study/MoveDetailModal";
import DownloadStudyModalCommon from "./components/Study/DownloadStudyModalCommon";
import { PatientId } from "./components/Patient/PatientId";
import { PatientNameGroup } from "./components/Patient/PatientNameGroup";
import { AccessionNumber } from "./components/AccessionNumber";
import { Indicator } from "./components/Indicator";
import { ErrorMessageValidator } from "./components/Displays/ErrorMessageValidator";
import AHSAsyncSelect, {
  AsyncSelectWrapperProps,
} from "./components/Select/AHSAsyncSelect";
import { AuthorizationSelect } from "./components/Select/AuthorizationSelect";
import { AppPurpose } from "./components/AppPurpose";
import ReactSelectSearch from "./components/Select/ReactSelectSearch";
import { UnitTimeSelect } from "./components/Select/UnitTimeSelect";
import { CustomSwitch } from "./components/Select/CustomSwitch";
import { UnitDaySelect } from "./components/Select/UnitDaySelect";
import { IncludeStringMaskSelect } from "./components/Select/IncludeStringMaskSelect";
import { IncludeDateMaskSelect } from "./components/Select/IncludeDateMaskSelect";
import LaddaButton from "./components/Button/LaddaButton";
import { FilterWrapper } from "./components/FilterWrapper";
import { ConfirmModal } from "./components/Modal/ConfirmModal";
import { FormModal } from "./components/Modal/FormModal";
import { ConfirmationPasswordModal } from "./components/Modal/ConfirmationPasswordModal";
import Switch from "./components/Switch";
import { SomeOfflineStorageClassModal } from "./components/Modal/SomeOfflineStorageClassModal";
import { ConfirmDeleteModal } from "./components/Modal/ConfirmDeleteModal";
import { ConfirmTypingDeleteModal } from "./components/Modal/ConfirmTypingDeleteModal";
import { ShareConfirmationPasswordModal } from "./components/Modal/ShareConfirmationPasswordModal";
import { SmsVerificationModal } from "./components/Modal/SmsVerificationModal";
import SensitiveModal from "./components/Study/SensitiveModal";
import MobileNumberSelect from "./components/Select/MobileNumberSelect";
import { PasswordEyeField } from "./components/Displays/PasswordEyeField";
import { PatientFuzzyInput } from "./components/Patient/PatientFuzzyInput";
import { PatientName as PatientNameCommon } from "./components/Patient/PatientName";
import { TimeRangePicker, DateRangePicker } from "./components/DatetimeDisplay";
import { ConfirmationDisclaimerModal } from "./components/Modal/ConfirmationDisclaimerModal";
import { RenderCriticalResultIcon } from "./components/Modal/RenderCRIcon";
import { PasswordField } from "./components/Displays/PasswordField";
import NavigationInterceptor from "./components/NavigationInterceptor";
import { useDetectReload } from "./hooks/useDetectReload";
import { useEffectOnceWhen } from "./hooks/useEffectOnceWhen";
import { useEffectOnce } from "./hooks/useEffectOne";
import { useOptionsMap } from "./hooks/useOptionsMap";
import { useIntervalTask } from "./hooks/useIntervalTask";
import { useScript } from "./hooks/useScript";
import useTurnstile from "./hooks/useTurnstile";
import { useLogger } from "./hooks/useLogger";
import {
  useStudyDatePreferenceDropdown,
  StudyDatePreference,
} from "./hooks/useStudyDatePreferenceDropdown";
import { CodeManagementForm } from "./components/FormRef/CodeManagementForm";
import { FormatLocalAELabel } from "./components/Displays/FormatLocalAELabel";
import { FormatRemoteAELabel } from "./components/Displays/FormatRemoteAELabel";
import {
  FormatCustomAELabel,
  filterCustomAEOption,
} from "./components/Displays/FormatCustomAELabel";
import { SupportConfirmModal } from "./components/Modal/SupportConfirmModal";
import type { RadPairEditorRef } from "./components/Editor/RadpairEditor";
import {
  Ago,
  DateFormat,
  DateTimeFormat as DateTimeDisplayFormat,
  TimeFormat,
  PresetDateRange,
} from "./components/DatetimeDisplay";
import { ChatWindow } from "./components/Chat";

import { RefreshButton } from "./components/Button/RefreshButton";
import { Badge, DropdownBadge } from "./components/Badge";
import type {
  BadgeProps,
  BadgeVariant,
  BadgeShape,
  BadgeSize,
  DropdownBadgeProps,
  DropdownBadgeItem,
} from "./components/Badge";

// WSS Events type
import type { DiagnosticReportUpdateEvent } from "./types/events";

/**
 * Services
 */
import DicomDictionaryService from "./services/storage/dicom_dictionary_service";
import { initJsStore } from "./services/storage/idb_service";
import { SocialProvider } from "./components/Button/ConnectButton";
export { SocialProvider };
export { DicomDictionaryService, initJsStore };
/**
 * Code Management
 */

import type {
  ICodeManagementForm,
  ICodeRequest,
  CreateCodingSystemRequest,
} from "./types/codeManagement";
export type { ICodeManagementForm, ICodeRequest, CreateCodingSystemRequest };

import { defaultCodeManagementValue } from "./types/codeManagement";

import type { ICodeTable, ICodeTableSearch } from "./types/codeTable";
export type { ICodeTable, ICodeTableSearch };

import { FindingsStatus } from "./types/priorFindings";
import type {
  IFinding,
  IFindings,
  IPriorFindingReport,
} from "./types/priorFindings";
export { FindingsStatus };
export type { IFinding, IFindings, IPriorFindingReport };
import { defaultCodeTableValue } from "./types/codeTable";

import type {
  ICodeTableMapping,
  ICodeTableMappingSearch,
  ICodeTableMappingUpdateState,
} from "./types/codeTableMapping";
export type {
  ICodeTableMapping,
  ICodeTableMappingSearch,
  ICodeTableMappingUpdateState,
};
import { defaultCodeTableMappingValue } from "./types/codeTableMapping";

// @ts-ignore
import type { IRvu } from "./types/Rvu";
export type { IRvu };

/**
 * Type Dicom
 */

import type { VersionData, DictionaryData } from "./types/dicom";
export type { VersionData, DictionaryData };

import type { DynamicSettingColumnData } from "./types/components/datatable";
export type { DynamicSettingColumnData };

/** Rule Builder, TransformRuleBuilder */

import RuleBuilder from "./components/Rules/RuleBuilder";
import TransformBuilder from "./components/Transforms";

export { RuleBuilder, TransformBuilder };

import { existScriptInTransformBuilderRule } from "./components/Transforms/helpers";
import AnonymizeComponent from "./components/Transforms/Fields/Anonymize";
import { existScriptInConditionRule } from "./components/Rules/helpers";
export { existScriptInTransformBuilderRule, existScriptInConditionRule };

// browser utils
import faroLogger from "./utils/faro";
import {
  isAndroid,
  isChrome,
  isIOSChrome,
  isIPhone,
  isSamsungBrowser,
  isSafariBrowser,
  buildPopupFeatures,
} from "./utils/browser";

// meddream
import {
  cancelPendingMeddreamReady,
  waitForMeddreamReady,
} from "./utils/meddreamReady";

// recaptcha
import {
  getRecaptchaToken,
  retryOnRecaptchaInvalid,
  toLoginRateLimitError,
  isLoginRateLimited,
  CaptchaProvider,
  detectCaptchaFromScript,
  checkGoogleReachable,
  exposeCaptchaHeaders,
} from "./utils/recaptchaUtils";

export type { CaptchaProvider };

// network error handling
import {
  NETWORK_ERROR_CODE,
  NETWORK_ERROR_MESSAGE,
  isBrowserOffline,
  isNetworkError,
  isNetworkErrorPayload,
  isNetworkErrorMessage,
  getNetworkErrorMessage,
  toNetworkError,
  setNetworkAlertEnabled,
  subscribeOfflineRequestFailure,
  subscribeRequestError,
  isRequestErrorVisible,
  dismissRequestError,
  clearRequestErrors,
  notifyRequestSucceeded,
  handleNetworkFailure,
} from "./utils/networkError";
import {
  NetworkStatusAlert,
  NetworkRequestErrorAlert,
} from "./components/NetworkStatusAlert";
export type { NetworkSource } from "./utils/networkError";
export {
  NETWORK_ERROR_CODE,
  NETWORK_ERROR_MESSAGE,
  isBrowserOffline,
  isNetworkError,
  isNetworkErrorPayload,
  isNetworkErrorMessage,
  getNetworkErrorMessage,
  toNetworkError,
  setNetworkAlertEnabled,
  subscribeOfflineRequestFailure,
  subscribeRequestError,
  isRequestErrorVisible,
  dismissRequestError,
  clearRequestErrors,
  notifyRequestSucceeded,
  handleNetworkFailure,
  NetworkStatusAlert,
  NetworkRequestErrorAlert,
};

// sentry series
import BrowserError from "./config/BrowserError";
import {
  initializeSentry,
  setSentryApplicationContext,
  setSentryTags,
  captureExceptionToSentry,
  captureEventToSentry,
} from "./config/sentry";
export {
  initializeSentry,
  setSentryApplicationContext,
  setSentryTags,
  captureExceptionToSentry,
  captureEventToSentry,
};
/**
 * Component Input
 */

import DicomInput from "./components/Input/DicomInput";
import HL7FieldInput from "./components/Input/HL7FieldInput";

export { DicomInput, HL7FieldInput, AnonymizeComponent };

/**
 * Support Doc Link
 */
import TermAndServiceContent from "./components/TermAndServiceContent";
import SupportDocsLink from "./components/SupportDocsLink";
export { SupportDocsLink, TermAndServiceContent };

/** Hooks */
import useAnonymizeDropdown from "./hooks/useAnonymizeDropdown";
export { useAnonymizeDropdown };

import useNameSegmentationPreferenceDropdown from "./hooks/useNameSegmentationPreferenceDropdown";
export { useNameSegmentationPreferenceDropdown };

/** types **/
import {
  IGateways,
  IDicomWebConfiguration,
  IDicomWebConfigurationState,
  IGateway,
  IGatewayState,
  IDicomWebConfigurationUpdate,
  IDimseTimeouts,
  IUpdateGateway,
  IGatewayList,
  RegionResponse,
  IAccessKeyResponse,
  IGatewaySearch,
  IGatewayInstaller,
  IGatewayInstallers,
  IGatewayInstallerLink,
  defaultValueDicomWebConfiguration,
  defaultValueDimseTimeouts,
  DIMSE_TIMEOUT_MIN_SECONDS,
  DIMSE_TIMEOUT_MAX_SECONDS,
  defaultSchedule,
  defaultValueGateway,
  defaultValueGatewayState,
  defaultValueGateways,
  defaultValueAccessKeyResponse,
  IGatewayLogSearch,
  IGatewayLog,
} from "./types/gateway";

import { RecaptchaTerm } from "./components/RecaptchaTerm";
import { RecaptchaTermNoTranslate } from "./components/RecaptchaTermNoTranslate";

/** Tasks **/
import {
  ITaskConfiguration,
  IUpdateTaskConfiguration,
  ITaskConfigurationSearch,
  defaultValueTaskConfiguration,
  ITask,
  TaskSearchState,
  defaultValueTask,
} from "./types/tasks";

/** SLA Type */
import type {
  ISLAResponse,
  ISLARequest,
  ISLAResponseNextRequest,
  ISLARuleResponse,
  ISLARuleRequest,
} from "./types/sla";

import { defaultSLA, defaultSLARule } from "./types/sla";

export type {
  ISLAResponse,
  ISLARequest,
  ISLAResponseNextRequest,
  ISLARuleResponse,
  ISLARuleRequest,
};

import type {
  SAMLConfigurationResponse,
  SAMLSPResponse,
  SAMLConfigurationRequest,
} from "./types/samlConfiguration";
import type {
  ICustomerHeader,
  IBasicAuthorization,
  IDataSource,
  IAuthorizationPayload,
  IAuthorizationType,
  DatasourceSearch,
  IDataSourceRequest,
} from "./types/datasource";
import { defaultDatasource } from "./types/datasource";
import {
  IWebhook,
  IWebhookRequest,
  WebhookSearch,
  defaultWebhook,
} from "./types/webhook";
import type {
  IMobileNumber,
  ReactSelectStates,
} from "./types/components/select";
import { defaultIMobileNumber } from "./types/components/select";
import { defaultValue as defaultLocalAe } from "./types/localAE";
import {
  PageMode,
  defaultSAMLRequest,
  defaultValue as defaultSAMLConfigResponse,
} from "./types/samlConfiguration";
export { PageMode, defaultSAMLRequest, defaultSAMLConfigResponse };
export type {
  SAMLConfigurationResponse,
  SAMLSPResponse,
  SAMLConfigurationRequest,
};

import type {
  IRole,
  IRoleDropdown,
  IEUnityRole,
  IRoleRequestModel,
  IRoleSearch,
} from "./types/role";
import { defaultEUnityValue, defaultRoleValue } from "./types/role";
import type {
  IPages,
  ISub,
  IPermission,
  defaultPermissionValue,
} from "./types/permission";
import type {
  OptionTypeBase,
  DropDownOption,
  ISearchFAResult,
  PendingAction,
} from "./types/common";

import type {
  NavGroupProps,
  NavItemProps,
  SidebarNavData,
  SidebarNavItem,
  NavbarProps,
  SidebarProps,
  AdminSideBarProps,
} from "./types";
import type {
  ReactDatePickerProps,
  Locales,
  DateRange,
  DateTimeFormat,
  ReactDateRangeProps,
  ReactTimeRangeProps,
  TimeRange,
} from "./types/components/datetime";
import type {
  ConfirmDeleteModalProps,
  ConfirmModalProps,
  FormModalProps,
  SimpleModalProps,
  BackModalProps,
} from "./types/components/modal";
import type {
  HL7FieldInputProps,
  DicomInputProps,
  TransferSyntaxInputProps,
} from "./types/components/input";
import type {
  SimpleAlertPropTypes,
  ErrorAlertPropTypes,
} from "./types/components/alert";
import type {
  ConstraintViolationError,
  IConstraintViolationMessage,
  IErrorMessage,
  AuthenticationErrorMessage,
  NestedErrorMessage,
  NestedErrorValue,
  ErrorMessage,
  HttpApiError,
  ErrorMessageWithParams,
  IBrowserError,
} from "./types/components/errorMessage";
import type {
  IPaginationBaseState,
  Sortable,
  Pageable,
  SlicePageable,
  SlicePaginationResponse,
  PaginationResponse,
  GenericPageable,
} from "./types/components/pagination";
import {
  defaultPaginationBaseValue,
  initPageable,
  initSlicePageable,
  initSearchPaginateState,
  initGenericPageable,
} from "./types/components/pagination";
import { Issuer, defaultValueOfIssuer } from "./types/issuer";
import {
  IOrganisation,
  IOrganisationSearch,
  IOrganisationList,
  defaultValueOfOrganisation,
} from "./types/organisation";
import {
  IHL7ServiceList,
  IHL7ServiceSearch,
  IMessageMappingList,
  ITriggerEventDropDown,
  IMessageMappingModel,
  RetentionTime,
  HLServiceMessageMappingType,
  HL7Service,
  defaultValueHl7Service,
  defaultValueHl7OutboundService,
  HL7OutboundInterfaceVersion,
  HL7Version,
  MessageTypeModel,
  TriggerEvents,
  DataMapping,
  TriggerEventsResponse,
  InterfaceVersionResponse,
  HL7ServiceMessageMappingModel,
  defaultCodingSystem,
  CodingSystem,
  WorklistStatusResponse,
  HL7OutboundService,
  HL7OutboundServiceType,
  IHL7OutboundServiceList,
} from "./types/hl7Services";
import {
  SelectChangeFunction,
  SelectChangeFunctionMessageMapping,
  IActionMeta,
  ReactSelectGroup,
  ReactSelectSearchProps,
  ReactSelectOptionProps,
  ReactSelectOptionGenderProps,
  ReactSelectCustomAEOptionProps,
} from "./types/components/reactSelectSearchProps";
import type { IButton } from "./types/components/button";
import type {
  ITableSelectionRef,
  Metadata,
  TableColumnResize,
  TableSortKey,
  ITableRef,
  RowProps,
  TableData,
  TableBuilderData,
  FooterData,
  SearchPaginateType,
  OnResizedChange,
  OnSortedChange,
  OnHideCols,
  IResourceLock,
  TableSection,
} from "./types/components/datatable";
import { TableDataAction } from "./types/components/datatable";
import { MissingRequestParameterError } from "./types/components/errorMessage";
import type {
  LaddaButtonParams,
  OnClickHandler,
  OnClickHandlerWithParams,
  MouseEvent,
} from "./types/components/laddaButton";

export type { AsyncSelectWrapperProps };

import {
  ConditionError,
  ConditionData,
  DicomRuleConditionData,
  StudyDescriptionConditionRule,
  DayOfWeekRuleConditionData,
  TimeOfDayRuleConditionData,
  ScriptRuleConditionData,
  HL7ServiceRuleConditionData,
  ConditionObjectData,
  TransformObjectData,
  ConditionRuleData,
  UntaggedConditionObjectData,
  Anonymize,
  defaultAnonymize,
  TransformData,
  TransformResponseData,
  HL7ServiceData,
  UntaggedTransformObjectResponse,
  UntaggedTransformObjectData,
  UntaggedConditionRuleData,
  ConditionFieldProps,
  HL7ServiceFieldProps,
  TransformFieldProps,
  TransformTypeEnum,
  Validator,
  RuleBuilderProps,
  TransformBuilderProps,
  ConditionProps,
  ConditionRuleProps,
  TransformerProps,
  DroppedConditionData,
  TransformProps,
  PREFETCHING_RULE_CONDITIONS,
  PREFETCHING_RULE_FILTER,
  PREFETCHING_RULE_QUERY,
  DynamicRuleConditionData,
} from "./types/rule";
/** utils **/
import {
  formatBytes,
  formatLocalAEName,
  formatRemoteAEName,
  getFullName,
  getHumanName,
  formatDateDicom,
  convertDateAndTimeToRequestTz,
  getReportContentTypeLabel,
  getReportContentByType,
  expandHtmlPlainTextCompatibleContents,
  isViewAsTextContent,
  isEmptyRichTextContent,
  normalizeRichTextContent,
  getRenderKey,
  getDiagnosticReportOptions,
  convertHighestTimeUnit,
  convertUnitTImeMinutesToString,
  convertIssuersToOptionTypeBase,
  convertHighestDayUnit,
  getLabelFromOrderPriority,
  getModalityDescription,
} from "./utils/format";
import {
  isValid,
  getCurrentDayInWeek,
  getCurrentTimeInDay,
  getDateInMonth,
  getMonthInYear,
  calculateRemainingMinutes,
} from "./utils/date";
import { useSiteReactSelectStyles } from "./hooks/useSiteReactSelectStyles";
import { useStatusDropdown } from "./hooks/useStatusDropDown";
import { useHistoryGoBack } from "./hooks/useHistoryGoBack";
import { useGotoList } from "./hooks/useGoToList";
import useAuthorizationTypeOption, {
  AuthorizationType,
} from "./hooks/useAuthorizationTypeOption";
import useTimezoneOptions from "./hooks/useTimezoneOptions";
import useSexDropdown from "./hooks/useSexDropdown";
import usePatientClassOptions from "./hooks/usePatientClassOptions";
import useFhirStatusOptions from "./hooks/useFhirReportStatusOptions";
import useGroupSiteOptions from "./hooks/useGroupSiteOptions";
import { useClinicianStatusDropdown } from "./hooks/useClinicianStatusDropdown";
import { useProcedurePriorityOptions } from "./hooks/useProcedurePriorityOptions";
import {
  emailValidator,
  SignUpFormValidator,
  URL_PATTERN,
  isValidURL,
} from "./utils/validators";
import { getDateRangeFromPresetConfig } from "./utils/presets";
import {
  convertToPermissionOptions,
  filterIntegrationByFeatureStatus,
  getStyleTable,
  setHours,
  routeLoader,
  formatDate,
  formatTime,
  setEndDate,
  formatDateRange,
  range,
  hasPermission,
  hasAllOfflineStorageClass,
  hasSomeOfflineStorageClass,
  parseDateFromString,
  blobToBase64,
  isOwner,
  parsePatientNameDicom,
  retryIfFailed,
  capitalizeFirstLetter,
  getNextHigherIndex,
  getNextLowerIndex,
  getSortKeysRequest,
  getSortKeyWithoutOrdersRoot,
  formatAssignedUser,
  formatAudioDurationDisplay,
  formatMobileNumber,
  getGroupSiteOptions,
  graceFullyPhoneFormat,
  prodModeLog,
  getBlobUrlForPDFPreview,
  isDay,
  isSafari,
  localStorageHasValue,
  getOrderProcedurePriorityIcon,
  generateCurrentTime,
  reverseString,
  copyLinkWithOrigin,
  configurationLinkDetails,
  adminLinkDetails,
  worklistAdminLinkDetails,
  trimObject,
  enumKeyToValue,
  enumValueToKey,
  enumKeysToValues,
  enumValuesToKeys,
  normalizeDateRange,
  removeFHIRCastTopic,
  generateAdvaViewPath,
  parseReportPriorityIcon,
} from "./utils/utils";
export type { Account } from "./utils/utils";

/** helpers **/
import {
  utcToLocal,
  utcToLocalDisplay,
  groupBy,
  getNumberOfDaysFromCurrentNumber,
  getNumberOfDaysFromStartedNumber,
  getOperatingSystem,
  createOptions,
  storageClassesContainOnline,
  convertDateToStringRequest,
  formatDateTime,
  partStringToTime,
  formatRegionUrl,
  formatValidDate,
  convertKBToMB,
  convertToOptionTypeBase,
  swapArrayItemWithIndex,
  getSessionValueWithRetry,
} from "./helpers";

/** constants **/
import {
  PERMISSIONS,
  CustomerPACSStatus,
  REGEX_PASSWORD,
  URL_REGEX,
  REGEX_EMAIL,
  REGEX_MOBILE_NUMBER,
  DemographicMismatchBehavior,
  FORMAT_IN_ZONED,
  DEFAULT_TIMEZONE,
  DEFAULT_SETTING_AUTO_RELOAD,
  DELAY_TIME_LOCK_SCREENS,
  TimeUnit,
  PatientClassType,
  EquivalenceType,
  TimeUnitOptions,
  getTimeUnitOptions,
  DayUnitOptions,
  DELAY_TIME_SEND_WSS,
  SUPPORT_PORTAL_URL,
  SendType,
  YYYYMMDD,
  yyyyMMdd,
  MMddyyyy,
  ddMMyyyy,
  yyyyMMddDashed,
  DayOfWeeks,
  Frequency,
  DiagnosticReportType,
  RadpairReportAction,
  HHmm,
  HHmmss,
  MIME_FILES_ACCEPT,
  Sex,
  WorklistViewer,
  ShareStudyMode,
  MODALITIES,
  SIDEBAR_STORAGE,
  OperatingSystem,
  FileType,
  COUNTRY_CODE_ISO,
  DAYS,
  StorageClass,
  DateRangePresets,
  Region,
  DATE_NATIVE_FORMAT,
  ReportStatusEnum,
  DiagnosticReportStatusEnum,
  FhirReportStatusEnum,
  WorklistReportStatus,
  MessageTypeEnum,
  PresenceStatusEnum,
  FHIRSystemType,
  TranscriptionLayoutLandscape,
  TranscriptionLayoutPortrait,
  TypingLayoutLandscape,
  TypingLayoutPortrait,
  RadpairLayoutLandscape,
  RadpairLayoutPortrait,
  PeerReviewMonitorLayoutLandscape,
  DictationLayoutLandscape,
  DictationLayoutPortrait,
  LayoutItem,
  LayoutLocalStorage,
  ContentType,
  WorklistResizeObject,
  ApplicationEntityType,
  DayTimeOfWeeksType,
  UploadMode,
  CMoveMode,
  Action,
  DataSourceType,
  CONSTANT,
  EQUAL,
  NOT_EQUAL,
  BETWEEN,
  BETWEEN_DATE_AND_TIME_INTERVAL,
  BETWEEN_DATE_INTERVAL,
  BETWEEN_TIME_INTERVAL,
  NOT_BETWEEN,
  GREATER,
  GREATER_OR_EQUAL,
  HL7,
  IN_RANGE,
  LESS,
  LESS_OR_EQUAL,
  LIKE,
  NOT_CONTAINS,
  NOT_LIKE,
  NOT_EMPTY,
  NOT_EXISTS,
  OUT_OF_RANGE,
  DATE_AND_TIME_INTERVAL,
  DATE_INTERVAL,
  DAY_OF_WEEK,
  DYNAMIC,
  AI_RELEVANCY,
  TIME_INTERVAL,
  TIME_OF_DAY,
  TaskStatus,
  SCRIPT,
  DICOM,
  CONTAINS,
  HL7_FIELD_FORMAT,
  DICOM_DICT_VERSION,
  DICOM_TAG_FORMAT,
  DYNAMIC_DURATION_PATTERN,
  DYNAMIC_FORMAT_DATE_AND_TIME,
  DYNAMIC_PERIOD_PATTERN,
  PATH_NAME_PATTERN,
  TIME,
  DATE_RANGE,
  ANONYMIZE,
  TIME_RANGE,
  DATE_AND_TIME,
  ThemeSetting,
  DATE,
  REGEX,
  REGEX_PATH,
  COPY,
  REMOVE,
  DICOM_INPUT,
  CURRENT_DATE,
  SLAType,
  DATE_AND_TIME_RANGE,
  SELECT_DARK_STYLES,
  REACT_SELECT_COLORS,
  MODALITY,
  BODY_PART,
  EXAM_CODE,
  AGE_RANGE,
  GENDER,
  STUDY_DESCRIPTION,
  SITE,
  ORGANISATION,
  REPORTING_PRIORITY,
  PATIENT_CLASS,
  StudiesResizeObject,
  getFooterSelectStyles,
  getSelectStyles,
  RecaptchaTokenAction,
  SeriesFilterRuleType,
  LIMIT_REPORT_SIZE,
  LIMIT_REPORT_MB_SIZE,
  SentryFilterStatus,
  OrderPriority,
  OrderProcedurePriority,
  OauthAction,
  MAX_WIDTH_MOBILE,
  MAX_WIDTH_TABLET,
  MIN_WIDTH_DESKTOP,
  MIN_WIDTH_TABLET,
  NameSegmentationPreference,
  RECAPTCHA_MAX_RETRIES,
  RECAPTCHA_RETRY_DELAY_MS,
  RECAPTCHA_SCRIPT_LOAD_RETRIES,
  ReportPriorityIconEnum,
  CriticalResultStatus,
  CriticalResultTabKey,
  ViewDiagnosticReportMode,
  ERROR_CODE,
  ADVAPACS_SUBSCRIBER_NAME_PREFIX,
  FaroFeatures,
  MAX_SIZE_QUERY,
} from "./constant";

// @ts-ignore
import * as ZedWhite from "./assets/images/logo/zed/white.svg";
// @ts-ignore
import * as ZedColor from "./assets/images/logo/zed/color.svg";

/**
 * Application Entities Types
 */
import type {
  ApplicationEntity,
  RemoteAESearch,
  RemoteAEResponse,
  RemoteAERequest,
  RemoteAEFormProps,
  AERemoteTablePropsType,
  RemoteApplicationEntityLightResponse,
  RemoteParams,
  IRemoteWorklistConfiguration,
  IRemoteWorklistConfigurationState,
  RemoteApplicationEntitySCUTransferSyntax,
} from "./types/ae";

/**
 * Image lifecycle management type
 */

import type {
  ImmutableStorageRulesSearch,
  ImmutableStorageRuleRequest,
  ImmutableStorageRuleResponse,
} from "./types/imageLifecycle";
import { defaultImmutableStorageRule } from "./types/imageLifecycle";

export type {
  ImmutableStorageRulesSearch,
  ImmutableStorageRuleRequest,
  ImmutableStorageRuleResponse,
};
export { defaultImmutableStorageRule };

/**
 * Certificate Types
 */
import type {
  CertificateRequest,
  CertificateResponse,
} from "./types/certificate";

import type { UserLightResponse } from "./types/user";
export type { UserLightResponse };
import type { IShowHideAttributeProps } from "./types/components/toolbarPlugin";
import { DefaultEnableToolbarEditorPluginAttributes } from "./types/components/toolbarPlugin";
import { initSearch } from "./types/tasks";

import type { IdSequenceLightResponse } from "./types/idSequence";
export type { IdSequenceLightResponse };
export type { CertificateRequest, CertificateResponse };

export type {
  IRole,
  IRoleDropdown,
  IEUnityRole,
  IRoleRequestModel,
  IRoleSearch,
};

export {
  defaultEUnityValue,
  defaultRoleValue,
  defaultIMobileNumber,
  DefaultEnableToolbarEditorPluginAttributes,
  initSearch,
};

export type {
  ApplicationEntity,
  RemoteAESearch,
  RemoteAEResponse,
  RemoteAERequest,
  RemoteAEFormProps,
  AERemoteTablePropsType,
  RemoteApplicationEntityLightResponse,
  RemoteParams,
  IRemoteWorklistConfiguration,
  IRemoteWorklistConfigurationState,
  RemoteApplicationEntitySCUTransferSyntax,
};

/**
 * End Application Entities Types
 */

/**
 * Documents types
 */

import type {
  IDocument,
  IPatientDocument,
  IDocumentGroupByPatient,
  IPagingDocumentGroupByPatient,
  ILoincCode,
  DocumentSearchState as SearchStateDocument,
  DocumentSearchParams,
  LoincCodeSearchParams,
  DocumentRequest,
  DocumentEdit,
  UploadDocumentRequest,
  DocumentSearchFormProps as SearchFormPropsDocument,
  IDocumentState,
  IGroupPatientShowHide,
  PatientDocumentRequest,
  ICreateNewPatientModalProps,
  PatientStudySearchParams,
} from "./types/documents";

export type {
  IDocument,
  IPatientDocument,
  IDocumentGroupByPatient,
  IPagingDocumentGroupByPatient,
  ILoincCode,
  SearchStateDocument,
  DocumentSearchParams,
  LoincCodeSearchParams,
  DocumentRequest,
  DocumentEdit,
  UploadDocumentRequest,
  SearchFormPropsDocument,
  IDocumentState,
  IGroupPatientShowHide,
  PatientDocumentRequest,
  ICreateNewPatientModalProps,
  PatientStudySearchParams,
};

import { defaultMetadata, defaultPatientDocRequest } from "./types/documents";
export { defaultMetadata, defaultPatientDocRequest };

/**
 * End Document Types
 */

/**
 * Local Aes Types
 */
import type {
  LocalAESearch,
  LocalAE,
  LocalAEFormProps,
  GroupedLocalAE,
  LocalAESelectProps,
  RemoteAESelectProps,
  LocalApplicationEntityLightResponse,
  LocalParams,
} from "./types/localAE";

export type {
  LocalAESearch,
  LocalAE,
  LocalAEFormProps,
  GroupedLocalAE,
  LocalAESelectProps,
  RemoteAESelectProps,
  LocalApplicationEntityLightResponse,
  LocalParams,
};

export type { IMobileNumber, ReactSelectStates };
export { defaultLocalAe };

/**
 * End Local Aes Types
 */

/**
 * Migration Jobs Types
 */

import type {
  QueryGranularity,
  ScheduleDayTimeModel,
  DayTimeOfWeeksProps,
  MigrationJobResponse,
  MigrationJobStudyResponse,
  MigrationJob,
  RangeTimeOfDays,
  ListRangeTimeOfDay,
  ScheduleDayTimeRequest,
  MigrationJobRequest,
  MigrationJobsFormProps,
  MigrationJobSearch,
  IAtTimezoneSelect,
  IMigrationDateRange,
  IMonthFrequency,
  IRecurrence,
  IRecurrenceRepeat,
  IYearFrequency,
  RecurrenceConfigurationRequest,
} from "./types/migrationJobs";

import {
  MigrationJobType,
  RecurrenceRepeatType,
  defaultRecurrence,
} from "./types/migrationJobs";

export type {
  QueryGranularity,
  ScheduleDayTimeModel,
  DayTimeOfWeeksProps,
  MigrationJobResponse,
  MigrationJobStudyResponse,
  MigrationJob,
  RangeTimeOfDays,
  ListRangeTimeOfDay,
  ScheduleDayTimeRequest,
  MigrationJobRequest,
  MigrationJobsFormProps,
  MigrationJobSearch,
  IAtTimezoneSelect,
  IMigrationDateRange,
  IMonthFrequency,
  IRecurrence,
  IRecurrenceRepeat,
  IYearFrequency,
  RecurrenceConfigurationRequest,
};
export type {
  ICustomerHeader,
  IBasicAuthorization,
  IAuthorizationPayload,
  IAuthorizationType,
  IDataSource,
  IWebhook,
  IWebhookRequest,
  WebhookSearch,
  IDataSourceRequest,
  DatasourceSearch,
};

import {
  defaultValue as defaultValueOfMigrationJob,
  MigrationJobTab,
} from "./types/migrationJobs";
export { defaultValueOfMigrationJob, MigrationJobTab };
/**
 * End Migration Jobs Types
 */

/**
 * Patients types
 */
import type {
  IPatientResponse,
  IPatientIdResponse,
  PatientSearchState,
  PatientSearchParams,
  IPatientRequest,
  ICreateNewPatientState,
} from "./types/patient";

export type {
  IPatientResponse,
  IPatientIdResponse,
  PatientSearchState,
  PatientSearchParams,
  IPatientRequest,
  ICreateNewPatientState,
};

import type {
  CreateScimConfigurationRequest,
  ScimConfigurationDetailResponse,
  ScimConfigurationResponse,
  ScimConfigurationsResponse,
  ScimAccessKeyResponse,
  UserTemplateResponse,
} from "./types/scim";

export type {
  CreateScimConfigurationRequest,
  ScimConfigurationDetailResponse,
  ScimConfigurationResponse,
  ScimConfigurationsResponse,
  ScimAccessKeyResponse,
  UserTemplateResponse,
};

import type { ISite, ISiteSearch, ISiteList, GroupedSite } from "./types/site";

export type {
  ISite,
  ISiteSearch,
  ISiteList,
  GroupedSite,
  IPages,
  ISub,
  IPermission,
};

import { defaultSite } from "./types/site";
export { defaultSite, defaultPermissionValue };

/**
 * End Site Types
 */

/** Study Types */
import type {
  StudySearchParams,
  RoutingQueueSearchParams,
  RoutingQueueSearchState,
  UploadQueueSearchParams,
  UploadQueueSearchState,
  SearchState,
  SearchFormProps,
  RoutingQueueSearchFormProps,
  PaginateState,
  MoveStudyModel,
  DicomAttributeResponse,
  DicomTagDisplay,
  IStudyState,
  IRoutingQueueState,
  IUploadQueueState,
  StudySize,
  StudyQR,
  SeriesQR,
  IStudyDetail,
  ICustomerResponse,
  IPractitioner,
  IHumanName,
  IDiagnosticReport,
  IDiagnosticReportContent,
  IReportStudy,
  IStudyIndexedResponse,
  DeleteFailed,
  IRoutingQueueDetail,
  IUploadQueueDetail,
  MimaPatientProps,
  MimaAccessionNumberProps,
  Instance,
  StudyQueryRetrieve,
  Patient,
  ISeriesDetail,
  IMoveDetail,
  IUpdateMoveDetail,
  IUpdatePatientId,
  IUpdatedPatientRequest,
  IUpdatedStudyRequest,
  IUpdatedSeriesRequest,
  ShareStudy,
  ShareStudyState,
  IBreakGlassPatientResponse,
  IPractitionerSearch,
  IPractitionerManageRequest,
  IClinicianPractitioner,
  DownloadStudyModalCommonRequest,
  IStudyNote,
} from "./types/studies";

import { DownloadStudyTypeCommon } from "./types/studies";

import type { IdSequencesSearch } from "./types/idSequence";

import type {
  ChatContext,
  Contact,
  MessageDetailResponse,
  MessageDetailRequest,
  ConversationRequest,
  ConversationResponse,
  NumberOfNotification,
  Participant,
  UserPresence,
  SeenToMessage,
} from "./types/chat";

export type {
  StudySearchParams,
  RoutingQueueSearchParams,
  RoutingQueueSearchState,
  UploadQueueSearchParams,
  UploadQueueSearchState,
  SearchState,
  SearchFormProps,
  RoutingQueueSearchFormProps,
  PaginateState,
  MoveStudyModel,
  DicomAttributeResponse,
  DicomTagDisplay,
  IStudyState,
  IRoutingQueueState,
  IUploadQueueState,
  StudySize,
  StudyQR,
  SeriesQR,
  IStudyDetail,
  ICustomerResponse,
  IPractitioner,
  IClinicianPractitioner,
  DownloadStudyModalCommonRequest,
  IStudyNote,
  IHumanName,
  IDiagnosticReport,
  IDiagnosticReportContent,
  IReportStudy,
  IStudyIndexedResponse,
  DeleteFailed,
  IRoutingQueueDetail,
  IUploadQueueDetail,
  MimaPatientProps,
  MimaAccessionNumberProps,
  Instance,
  StudyQueryRetrieve,
  Patient,
  ISeriesDetail,
  IMoveDetail,
  IUpdateMoveDetail,
  IUpdatePatientId,
  IUpdatedPatientRequest,
  IUpdatedStudyRequest,
  IUpdatedSeriesRequest,
  ShareStudy,
  ShareStudyState,
  IBreakGlassPatientResponse,
  IPractitionerSearch,
  IdSequencesSearch,
  IPractitionerManageRequest,
  ChatContext,
  Contact,
  MessageDetailResponse,
  MessageDetailRequest,
  ConversationRequest,
  ConversationResponse,
  NumberOfNotification,
  Participant,
  UserPresence,
  SeenToMessage,
  IShowHideAttributeProps,
};

import {
  defaultMoveDetail,
  defaultPatientDetail,
  defaultSeriesDetail,
  defaultStudyDetail,
  defaultStudyQR,
  defaultPractitionersValue,
} from "./types/studies";

export {
  defaultMoveDetail,
  defaultPatientDetail,
  defaultSeriesDetail,
  defaultStudyDetail,
  defaultStudyQR,
  defaultPractitionersValue,
};

/** End Study Types */

/** Patient Portal */
import type {
  IPatientPortal,
  IPatientPortalRequest,
  IStudyOrderResponse,
  IStudyOrderDetailsResponse,
} from "./types/patientPortal";
import {
  defaultPatientPortal,
  defaultStudyOrderDetailsResponse,
} from "./types/patientPortal";
import type { EulaResponse } from "./types/eula";

export type {
  IPatientPortal,
  IPatientPortalRequest,
  IStudyOrderResponse,
  IStudyOrderDetailsResponse,
  EulaResponse,
};

import type {
  IUserGroupSearch,
  IMemberGroupSearch,
  IUserGroup,
  IUserGroupRequest,
  IUserGroupUpdateState,
  IUserByGroupSearch,
} from "./types/userGroup";

import { userGroupDefaultValue } from "./types/userGroup";

export type {
  IUserGroupSearch,
  IMemberGroupSearch,
  IUserGroup,
  IUserGroupRequest,
  IUserGroupUpdateState,
  IUserByGroupSearch,
};

/** Transfer Syntax Types */

import type { TransferSyntaxResponse } from "./types/transferSyntax";
export type { TransferSyntaxResponse };

import type { ClinicianUserResponse } from "./types/clinicianUser";
export type { ClinicianUserResponse };

/** End Transfer Syntax Types */
import type {
  IILMMonitorStudy,
  IILMMonitorSeries,
  IILMMonitorStudySearchParams,
  ILMSearchFormProps,
} from "./types/ilmMonitor";
export type {
  IILMMonitorStudy,
  IILMMonitorSeries,
  IILMMonitorStudySearchParams,
  ILMSearchFormProps,
};

/** IP Filters */
import type { CountryResponse } from "./types/ipfilter";
export type { CountryResponse };
/** End IP Filters */

import type { NexusAssetResponse } from "./types/nexus";
import ContactWrapper from "./components/Chat/ContactList/ContactWrapper";
export type { NexusAssetResponse };

import { ILanguages, AdvaPACSViewer } from "./types/settings";
import type { RegistrationRequestResponse } from "./types/mfa";
import { useThemeSystemDetect } from "./hooks/useThemeSystemDetect";
import CPBrandLogo from "./components/Logo/CPBrandLogo";
export type { RegistrationRequestResponse, ILanguages };
const AppLoader = {
  Loader,
  RestrictedIp,
  DomainError,
  AppError,
  ImageLoader,
  ViewerLoader,
};
const AppLogo = { LogoLong, LogoMain, LogoShort, CPLogoMain, CPBrandLogo };
const AppAlert = { SimpleAlert, ErrorAlert };

/** export types **/
export type {
  OptionTypeBase,
  IButton,
  LaddaButtonParams,
  OnClickHandler,
  OnClickHandlerWithParams,
  MouseEvent as ReactMouseEvent,
  DropDownOption,
  ISearchFAResult,
  PendingAction,
};
export type {
  ITableSelectionRef,
  Metadata,
  TableColumnResize,
  TableSortKey,
  ITableRef,
  RowProps,
  TableData,
  TableBuilderData,
  FooterData,
  SearchPaginateType,
  OnResizedChange,
  OnSortedChange,
  OnHideCols,
  IResourceLock,
  TableSection,
};
export type { SimpleAlertPropTypes, ErrorAlertPropTypes };
export type {
  ConditionError,
  ConditionData,
  DicomRuleConditionData,
  StudyDescriptionConditionRule,
  DayOfWeekRuleConditionData,
  TimeOfDayRuleConditionData,
  ScriptRuleConditionData,
  HL7ServiceRuleConditionData,
  ConditionObjectData,
  TransformObjectData,
  ConditionRuleData,
  UntaggedConditionObjectData,
  Anonymize,
  TransformData,
  TransformResponseData,
  HL7ServiceData,
  UntaggedTransformObjectResponse,
  UntaggedTransformObjectData,
  UntaggedConditionRuleData,
  ConditionFieldProps,
  HL7ServiceFieldProps,
  TransformFieldProps,
  Validator,
  RuleBuilderProps,
  TransformBuilderProps,
  ConditionProps,
  ConditionRuleProps,
  TransformerProps,
  DroppedConditionData,
  TransformProps,
  DynamicRuleConditionData,
};
export type {
  IPaginationBaseState,
  Sortable,
  Pageable,
  SlicePageable,
  SlicePaginationResponse,
  GenericPageable,
  PaginationResponse,
};
export type {
  ConstraintViolationError,
  IConstraintViolationMessage,
  IErrorMessage,
  AuthenticationErrorMessage,
  NestedErrorMessage,
  NestedErrorValue,
  ErrorMessage,
  HttpApiError,
  ErrorMessageWithParams,
  IBrowserError,
};
export type { HL7FieldInputProps, DicomInputProps, TransferSyntaxInputProps };
export type {
  ConfirmDeleteModalProps,
  ConfirmModalProps,
  FormModalProps,
  SimpleModalProps,
  BackModalProps,
};
export type {
  ReactDatePickerProps,
  Locales,
  DateRange,
  DateTimeFormat,
  ReactDateRangeProps,
  ReactTimeRangeProps,
  TimeRange,
};
export type {
  NavGroupProps,
  NavItemProps,
  SidebarNavData,
  SidebarNavItem,
  NavbarProps,
  SidebarProps,
  AdminSideBarProps,
};
export type {
  IGateways,
  IDicomWebConfiguration,
  IDicomWebConfigurationState,
  IGateway,
  IGatewayState,
  IDicomWebConfigurationUpdate,
  IDimseTimeouts,
  IUpdateGateway,
  IGatewayList,
  RegionResponse,
  IAccessKeyResponse,
  IGatewaySearch,
  IGatewayInstaller,
  IGatewayInstallers,
  IGatewayInstallerLink,
  IGatewayLogSearch,
  IGatewayLog,
};
export type {
  ITaskConfiguration,
  IUpdateTaskConfiguration,
  ITaskConfigurationSearch,
  ITask,
  TaskSearchState,
};
export type { Issuer, RadPairEditorRef };
export type { IOrganisation, IOrganisationSearch, IOrganisationList };
export type {
  IHL7ServiceList,
  IHL7ServiceSearch,
  IMessageMappingList,
  ITriggerEventDropDown,
  IMessageMappingModel,
  RetentionTime,
  HLServiceMessageMappingType,
  HL7Service,
  MessageTypeModel,
  TriggerEvents,
  DataMapping,
  TriggerEventsResponse,
  InterfaceVersionResponse,
  HL7ServiceMessageMappingModel,
  CodingSystem,
  WorklistStatusResponse,
  HL7OutboundService,
  IHL7OutboundServiceList,
};

export type {
  SelectChangeFunction,
  SelectChangeFunctionMessageMapping,
  IActionMeta,
  ReactSelectGroup,
  ReactSelectSearchProps,
  ReactSelectOptionProps,
  ReactSelectOptionGenderProps,
  ReactSelectCustomAEOptionProps,
  ITypingEditorRef,
  IconDetail,
};

// export components
export {
  Ago,
  AppAlert,
  TableDataAction,
  TypingEditor,
  AppLogo,
  AppLoader,
  AccessionNumber,
  UnitTimeSelect,
  CustomSwitch,
  UnitDaySelect,
  IncludeStringMaskSelect,
  IncludeDateMaskSelect,
  BlockUI,
  BigText,
  UploadFileDropArea,
  CommitedStatus,
  IdleWrapper,
  ContentWrapperComponent,
  ZedIcon,
  LockSolidIcon,
  Mobile3Icon,
  PassKeyIcon,
  DayOfWeeks,
  Frequency,
  DiagnosticReportType,
  RadpairReportAction,
  CommonDatePicker,
  DocumentViewer,
  DiagnosticReportFooter,
  DiagnosticReportHeader,
  DiagnosticReportButtons,
  HTMLViewer,
  ReportViewer,
  RadioButtonGroup,
  RenderCustomHeader,
  ConfirmModal,
  ConfirmationPasswordModal,
  MoveDetailModal,
  DownloadStudyModalCommon,
  ConfirmDeleteModal,
  ConfirmTypingDeleteModal,
  ConfirmationDisclaimerModal,
  SomeOfflineStorageClassModal,
  DateFormat,
  AHSAsyncSelect,
  AuthorizationSelect,
  Draggable,
  DropTarget,
  DateTimeDisplayFormat,
  ErrorMessageValidator,
  FilterWrapper,
  FormModal,
  PortalCard,
  ReportCard,
  ExamCard,
  OptionLabel,
  LaddaButton,
  TimeFormat,
  ZedColor,
  ZedWhite,
  PresetDateRange,
  SensitiveModal,
  HighlightedTextNotMatch,
  RadPairEditor,
  MultiLevelDropdown,
  StudyMultiLevelDropdown,
  PasskeyAuthenticateModal,
  AuthenticatorAppModal,
  MultiLevelDropdownButtons,
  BackButton,
  TableFooter,
  PAGE_SIZE_OPTION,
  TableView,
  Table,
  TableLazy,
  TableSelection,
  CustomizeSettingTable,
  PersonCircleXMarkIcon,
  TextTruncated,
  ReportPriorityDisplay,
  ReportPriorityIconDisplay,
  ReportPriorityIconPicker,
  ShowSearchRestrictionMessage,
  IconPicker,
  PermissionTreeSelect,
  ProgressBar,
  ProgressBarBadgeTooltip,
  PasswordRequirementTooltip,
  PasswordRequirements,
  PatientId,
  PatientNameGroup,
  ShowErrorMessage,
  Indicator,
  PasswordEyeField,
  PasswordField,
  FormatLocalAELabel,
  FormatRemoteAELabel,
  FormatCustomAELabel,
  filterCustomAEOption,
  defaultPaginationBaseValue,
  initSearchPaginateState,
  WorklistViewer,
  InformationTooltip,
  AHSTooltip,
  ShareConfirmationPasswordModal,
  Switch,
  RecaptchaTerm,
  RecaptchaTermNoTranslate,
  SmsVerificationModal,
  ReactSelectSearch,
  RenderCriticalResultIcon,
  SignUpFormValidator,
  MissingRequestParameterError,
  ContactWrapper,
  ChatWindow,
  MobileNumberSelect,
  IconDisplay,
  Card,
  PatientFuzzyInput,
  PatientNameCommon,
  FHIRSystemType,
  TimeRangePicker,
  DateRangePicker,
  MigrationJobType,
  DownloadStudyTypeCommon,
  RecurrenceRepeatType,
  NavigationInterceptor,
  CodeManagementForm,
  ConnectButton,
  AppPurpose,
  AdvaPACSPrivacyAndPolicy,
  SupportLink,
  TermsOfServiceLink,
  SupportConfirmModal,
  // component icon
  HighestIcon,
  MediumIcon,
  LowIcon,
  LowestIcon,
  HighIcon,
  RoutineIcon,
  GenerateAccessionNumber,
  RefreshButton,
  Badge,
  DropdownBadge,
};
export type {
  BadgeProps,
  BadgeVariant,
  BadgeShape,
  BadgeSize,
  DropdownBadgeProps,
  DropdownBadgeItem,
};

// export constant values
export {
  MAX_WIDTH_MOBILE,
  MAX_WIDTH_TABLET,
  MIN_WIDTH_DESKTOP,
  MIN_WIDTH_TABLET,
  OrderPriority,
  OrderProcedurePriority,
  OauthAction,
  Sex,
  ShareStudyMode,
  OperatingSystem,
  FileType,
  DAYS,
  StorageClass,
  TransformTypeEnum,
  DateRangePresets,
  ReportStatusEnum,
  CustomerPACSStatus,
  DemographicMismatchBehavior,
  Region,
  DiagnosticReportStatusEnum,
  FhirReportStatusEnum,
  WorklistReportStatus,
  MessageTypeEnum,
  PresenceStatusEnum,
  TypingLayoutLandscape,
  TypingLayoutPortrait,
  RadpairLayoutLandscape,
  RadpairLayoutPortrait,
  PeerReviewMonitorLayoutLandscape,
  TranscriptionLayoutLandscape,
  TranscriptionLayoutPortrait,
  DictationLayoutPortrait,
  DictationLayoutLandscape,
  LayoutItem,
  LayoutLocalStorage,
  ContentType,
  WorklistResizeObject,
  ApplicationEntityType,
  DayTimeOfWeeksType,
  DataSourceType,
  UploadMode,
  CMoveMode,
  CriticalResultStatus,
  CriticalResultTabKey,
  ViewDiagnosticReportMode,
  ERROR_CODE,
  Action,
  HL7OutboundServiceType,
  CONSTANT,
  EQUAL,
  NOT_EQUAL,
  BETWEEN,
  BETWEEN_DATE_AND_TIME_INTERVAL,
  BETWEEN_DATE_INTERVAL,
  BETWEEN_TIME_INTERVAL,
  NOT_BETWEEN,
  GREATER,
  GREATER_OR_EQUAL,
  HL7,
  IN_RANGE,
  LESS,
  LESS_OR_EQUAL,
  LIKE,
  NOT_CONTAINS,
  NOT_LIKE,
  NOT_EMPTY,
  NOT_EXISTS,
  OUT_OF_RANGE,
  DATE_AND_TIME_INTERVAL,
  DATE_INTERVAL,
  DAY_OF_WEEK,
  DYNAMIC,
  AI_RELEVANCY,
  TIME_INTERVAL,
  TIME_OF_DAY,
  TaskStatus,
  SCRIPT,
  DICOM,
  CONTAINS,
  HL7_FIELD_FORMAT,
  DICOM_DICT_VERSION,
  DICOM_TAG_FORMAT,
  DYNAMIC_DURATION_PATTERN,
  DYNAMIC_FORMAT_DATE_AND_TIME,
  DYNAMIC_PERIOD_PATTERN,
  PATH_NAME_PATTERN,
  TIME,
  DATE_RANGE,
  ANONYMIZE,
  TIME_RANGE,
  DATE_AND_TIME,
  DATE,
  REGEX,
  REGEX_PATH,
  COPY,
  REMOVE,
  DICOM_INPUT,
  CURRENT_DATE,
  SLAType,
  ThemeSetting,
  DATE_AND_TIME_RANGE,
  SELECT_DARK_STYLES,
  REACT_SELECT_COLORS,
  MODALITY,
  BODY_PART,
  EXAM_CODE,
  AGE_RANGE,
  GENDER,
  STUDY_DESCRIPTION,
  SITE,
  RecaptchaTokenAction,
  SeriesFilterRuleType,
  LIMIT_REPORT_SIZE,
  LIMIT_REPORT_MB_SIZE,
  SentryFilterStatus,
  NameSegmentationPreference,
  RECAPTCHA_MAX_RETRIES,
  RECAPTCHA_RETRY_DELAY_MS,
  RECAPTCHA_SCRIPT_LOAD_RETRIES,
  ADVAPACS_SUBSCRIBER_NAME_PREFIX,
  FaroFeatures,
  MAX_SIZE_QUERY,
  ReportPriorityIconEnum,
  ORGANISATION,
  REPORTING_PRIORITY,
  PATIENT_CLASS,
  StudiesResizeObject,
  MODALITIES,
  SIDEBAR_STORAGE,
  COUNTRY_CODE_ISO,
  REGEX_PASSWORD,
  REGEX_MOBILE_NUMBER,
  PERMISSIONS,
  REGEX_EMAIL,
  URL_REGEX,
  FORMAT_IN_ZONED,
  DEFAULT_TIMEZONE,
  DEFAULT_SETTING_AUTO_RELOAD,
  DELAY_TIME_LOCK_SCREENS,
  DELAY_TIME_SEND_WSS,
  TimeUnit,
  PatientClassType,
  EquivalenceType,
  TimeUnitOptions,
  getTimeUnitOptions,
  DayUnitOptions,
  SendType,
  SUPPORT_PORTAL_URL,
  PREFETCHING_RULE_CONDITIONS,
  PREFETCHING_RULE_FILTER,
  PREFETCHING_RULE_QUERY,
  DATE_NATIVE_FORMAT,
  MIME_FILES_ACCEPT,
  YYYYMMDD,
  yyyyMMdd,
  MMddyyyy,
  ddMMyyyy,
  yyyyMMddDashed,
  HHmm,
  HHmmss,
  HL7Version,
  AuthorizationType,
  AdvaPACSViewer,
};
// export default values
export {
  defaultAnonymize,
  defaultValueDicomWebConfiguration,
  defaultValueDimseTimeouts,
  DIMSE_TIMEOUT_MIN_SECONDS,
  DIMSE_TIMEOUT_MAX_SECONDS,
  defaultSchedule,
  defaultValueGateway,
  defaultValueGatewayState,
  defaultValueGateways,
  defaultValueTaskConfiguration,
  defaultValueAccessKeyResponse,
  defaultValueOfIssuer,
  defaultValueOfOrganisation,
  defaultValueHl7Service,
  HL7OutboundInterfaceVersion,
  defaultCodingSystem,
  defaultValueHl7OutboundService,
  initGenericPageable,
  initPageable,
  initSlicePageable,
  defaultRecurrence,
  defaultDatasource,
  defaultWebhook,
  defaultCodeManagementValue,
  defaultCodeTableValue,
  defaultCodeTableMappingValue,
};
// hooks
export {
  useLogger,
  useStudyDatePreferenceDropdown,
  useDetectReload,
  useEffectOnceWhen,
  useEffectOnce,
  useOptionsMap,
  useIntervalTask,
  useScript,
  useTurnstile,
  useStatusDropdown,
  useSiteReactSelectStyles,
  useTimezoneOptions,
  useHistoryGoBack,
  useGotoList,
  useAuthorizationTypeOption,
  useSexDropdown,
  usePatientClassOptions,
  useClinicianStatusDropdown,
  useProcedurePriorityOptions,
  useGroupSiteOptions,
  useThemeSystemDetect,
  userGroupDefaultValue,
  useCaptcha,
  useFhirStatusOptions,
};

// export functions
export {
  getFooterSelectStyles,
  getSelectStyles,
  isOwner,
  formatBytes,
  formatLocalAEName,
  getHumanName,
  formatDateDicom,
  convertDateAndTimeToRequestTz,
  getReportContentTypeLabel,
  getReportContentByType,
  expandHtmlPlainTextCompatibleContents,
  isViewAsTextContent,
  isEmptyRichTextContent,
  normalizeRichTextContent,
  getRenderKey,
  getDiagnosticReportOptions,
  convertHighestTimeUnit,
  convertUnitTImeMinutesToString,
  getModalityDescription,
  convertIssuersToOptionTypeBase,
  convertHighestDayUnit,
  getLabelFromOrderPriority,
  getBlobUrlForPDFPreview,
  isSafari,
  localStorageHasValue,
  getOrderProcedurePriorityIcon,
  generateCurrentTime,
  copyLinkWithOrigin,
  configurationLinkDetails,
  adminLinkDetails,
  worklistAdminLinkDetails,
  trimObject,
  enumKeyToValue,
  enumValueToKey,
  enumKeysToValues,
  enumValuesToKeys,
  reverseString,
  parsePatientNameDicom,
  capitalizeFirstLetter,
  retryIfFailed,
  getNextHigherIndex,
  getNextLowerIndex,
  getSortKeysRequest,
  getSortKeyWithoutOrdersRoot,
  formatAssignedUser,
  graceFullyPhoneFormat,
  prodModeLog,
  isDay,
  formatRemoteAEName,
  convertToPermissionOptions,
  filterIntegrationByFeatureStatus,
  getFullName,
  utcToLocal,
  utcToLocalDisplay,
  groupBy,
  getNumberOfDaysFromCurrentNumber,
  StudyDatePreference,
  getNumberOfDaysFromStartedNumber,
  getOperatingSystem,
  createOptions,
  storageClassesContainOnline,
  convertDateToStringRequest,
  formatDateTime,
  partStringToTime,
  getDateRangeFromPresetConfig,
  getStyleTable,
  routeLoader,
  setHours,
  formatRegionUrl,
  formatValidDate,
  convertToOptionTypeBase,
  swapArrayItemWithIndex,
  convertKBToMB,
  emailValidator,
  URL_PATTERN,
  isValidURL,
  formatDate,
  formatTime,
  setEndDate,
  formatDateRange,
  range,
  hasPermission,
  hasAllOfflineStorageClass,
  hasSomeOfflineStorageClass,
  parseDateFromString,
  blobToBase64,
  isValid,
  getCurrentDayInWeek,
  getDateInMonth,
  getMonthInYear,
  getCurrentTimeInDay,
  calculateRemainingMinutes,
  defaultSLA,
  defaultSLARule,
  defaultPatientPortal,
  defaultStudyOrderDetailsResponse,
  formatAudioDurationDisplay,
  formatMobileNumber,
  getGroupSiteOptions,
  defaultValueTask,
  normalizeDateRange,
  removeFHIRCastTopic,
  generateAdvaViewPath,
  parseReportPriorityIcon,
  getSessionValueWithRetry,
};
/** Export context */
export {
  MeddreamWindowContext,
  MeddreamWindow,
  CaptchaProviderContext,
  waitForMeddreamReady,
  cancelPendingMeddreamReady,
};
// icons

export {
  StudyFolderIcon,
  MinisIcon,
  PlusIcon,
  ViewerLightIcon,
  ViewerDarkIcon,
  GoogleIcon,
  AppleIcon,
  UserLargeIcon,
  RushIcon,
  UnRushIcon,
  StatIcon,
  HighIconSVG,
  RoutineIconSVG,
  RushLightIcon,
  UnRushLightIcon,
};

// FARO
export { faroLogger };

// class
export { BrowserError };

// util browser
export {
  isAndroid,
  isChrome,
  isIOSChrome,
  isIPhone,
  isSamsungBrowser,
  isSafariBrowser,
  buildPopupFeatures,
};

export {
  BrandCall,
  BrandLanguage,
  BrandLocation,
  BrandMail,
  BrandCallDark,
  BrandLanguageDark,
  BrandLocationDark,
  BrandMailDark,
};

// recaptcha utils
export {
  getRecaptchaToken,
  retryOnRecaptchaInvalid,
  toLoginRateLimitError,
  isLoginRateLimited,
  detectCaptchaFromScript,
  checkGoogleReachable,
  exposeCaptchaHeaders,
};

// web socket
export type {
  WebSocketContextType,
  WssFHIRCastEventPayload,
  DiagnosticReportUpdateEvent,
};
export { useWSS, WebSocketProvider, FHIRCastEvent };

/** AX-8466: shared types previously copied in the apps */
export { Storage } from "./utils/storage";
export { UsernameTransform } from "./hooks/useUsernameTransformDropDown";
export type { ILocalisation } from "./utils/utils";
export type { IGroup } from "./types/common";
export type { IGroupStudyShowHide } from "./types/ilmMonitor";
export type { IStudyLightDiagnosticReport, TagResponse } from "./types/studies";
export type { BoxItemResponse } from "./types/box";
export type { EulaDetailResponse } from "./types/eula";
export type { IconResponse } from "./types/customField";
export type { IAssignUserResponse } from "./types/users";
export { getRegion } from "./utils/utils";
export { HL7ServiceMode, HL7ServiceType } from "./constant";
export type {
  ICodeResponse,
  IReportPriority,
  IPatientOrderResponse,
  IOrderResponse,
  IssuerResponse,
} from "./types/order";
export type { IState } from "./types/site";
export { useCommonThemeConfig } from "./hooks/useCommonThemeConfig";
export {
  BrandProvider,
  useBrand,
  getBrandStyleButton,
} from "./context/BrandContext";
export type { IClinicianBrand } from "./types/brand";
export {
  ViewerButtonType,
  DateComponentsUnitOptions,
  MaskFromUnitOptions,
  PeerReviewFeedbackObject,
  PeerReviewMonitorObject,
  PeerReviewQueueObject,
} from "./constant";
